import { useState, useEffect, useCallback, useRef } from 'react'
import { useUser } from '@clerk/clerk-react'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { useThemeColors } from '../hooks/useThemeColors'

const ADMIN_EMAILS = ['matt@primateintelligence.ai', 'mehdi@primateintelligence.ai']
// PRI-271: centralized API base URL
// PRI-496 P0: admin endpoints are internal — route through the chokepoint.
import { internalFetch } from '../core/lib/internal-api-client'
import { getAuthToken } from '../core/lib/api/client'
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string

// ── Types ──────────────────────────────────────────────────────────────────────

interface JobStats {
  total: number; complete: number; failed: number; processing: number
  failureRate: number; avgDurationSeconds: number | null
  recentFailed: Array<{ id: string; error_message: string; created_at: string; prompt: string }>
}

interface InferenceMetrics {
  gpu_vram_used_mb: number; gpu_vram_free_mb: number; gpu_vram_total_mb: number
  gpu_vram_used_pct: number; queue_depth: number; uptime_seconds: number
  model_loaded: boolean; jobs_processed_total: number; jobs_failed_total: number
}

interface ModelInfo {
  model_name: string; vl_checkpoint: string; jepa_checkpoint: string
  vl_step: number; jepa_step: number; status?: string
}

interface CheckpointInfo { name: string; step: number; size_mb: number }

interface LatencyStats {
  p50: number | null; p95: number | null; avg: number | null; count: number
  timeSeries: Array<{ date: string; p50: number; p95: number; count: number }>
  jobs: { p50: number | null; p95: number | null; avg: number | null; count: number }
}

interface ApiLatencyRoute {
  route: string; p50: number | null; p95: number | null; avg: number | null
  count: number; errorRate: number
}

interface UptimeStats {
  uptime30d: number; uptime7d: number; downtimeSeconds30d: number
  incidents: Array<{ id: string; started_at: string; resolved_at: string | null; alarm_name: string; description: string | null; duration_seconds: number | null }>
  currentAlarms: string[]
}

interface CostStats {
  byDate: Record<string, Array<{ service: string; cost: number }>>
  total: number; liveData: Array<{ service: string; cost: number }> | null
  ceAccessible: boolean; note: string | null
}

interface CdnMetric {
  date: string; total_requests: number; cache_hit_requests: number
  cache_hit_rate: number; bytes_transferred: number; error_4xx_count: number; error_5xx_count: number
}

interface LogEvent {
  timestamp: number; message: string; logStreamName?: string
}

interface QueueHealthData {
  peakStreamDepth: number
  peakFileDepth: number
  utilizationPct: number
  queueFullSessions: number
  timeSeries: Array<{ hour: string; stream: number; file: number }>
}

interface SystemStatus {
  inferenceMetrics: InferenceMetrics | null
  modelInfo: ModelInfo | null
  railwayApi: 'up' | 'down' | 'unknown'
  jobsLast24h: JobStats
  checkedAt: string
}

// ── Data fetching ──────────────────────────────────────────────────────────────

async function fetchSystemStatus(): Promise<SystemStatus> {
  const headers: Record<string, string> = {
    apikey: SUPABASE_ANON_KEY ?? '',
    Authorization: `Bearer ${SUPABASE_ANON_KEY ?? ''}`,
    'Content-Type': 'application/json',
  }
  const cutoff24h = new Date(Date.now() - 24 * 3600 * 1000).toISOString()

  // PRI (2026-06-26): public /v1/model-info no longer returns checkpoint detail.
  // Admin DevOps still needs full provenance, so hit the admin-gated proxy with
  // the Clerk bearer token.
  const token = await getAuthToken()
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : undefined

  const [jobsRes, failedJobsRes, railwayHealthRes, metricsRes, modelInfoRes] = await Promise.allSettled([
    fetch(`${SUPABASE_URL}/rest/v1/jobs?created_at=gte.${cutoff24h}&select=status,duration_seconds,error_message,created_at,prompt,id`, { headers }),
    fetch(`${SUPABASE_URL}/rest/v1/jobs?status=eq.failed&created_at=gte.${cutoff24h}&select=id,error_message,created_at,prompt&order=created_at.desc&limit=5`, { headers }),
    internalFetch(`/health`, { signal: AbortSignal.timeout(5000) }),
    internalFetch(`/v1/metrics`, { signal: AbortSignal.timeout(5000) }),
    internalFetch(`/v1/model-info-proxy`, { headers: authHeaders, signal: AbortSignal.timeout(5000) }),
  ])

  let jobsData: Array<{ status: string; duration_seconds: number | null }> = []
  let recentFailed: Array<{ id: string; error_message: string; created_at: string; prompt: string }> = []
  if (jobsRes.status === 'fulfilled' && jobsRes.value.ok) jobsData = await jobsRes.value.json()
  if (failedJobsRes.status === 'fulfilled' && failedJobsRes.value.ok) recentFailed = await failedJobsRes.value.json()

  const total = jobsData.length
  const complete = jobsData.filter(j => j.status === 'complete').length
  const failed = jobsData.filter(j => j.status === 'failed').length
  const processing = jobsData.filter(j => j.status === 'processing').length
  const durations = jobsData.filter(j => j.duration_seconds != null).map(j => j.duration_seconds!)
  const avgDuration = durations.length > 0 ? durations.reduce((a, b) => a + b, 0) / durations.length : null

  let railwayApi: 'up' | 'down' | 'unknown' = 'unknown'
  if (railwayHealthRes.status === 'fulfilled') railwayApi = railwayHealthRes.value.ok ? 'up' : 'down'
  else railwayApi = 'down'

  let inferenceMetrics: InferenceMetrics | null = null
  if (metricsRes.status === 'fulfilled' && metricsRes.value.ok) {
    inferenceMetrics = await metricsRes.value.json() as InferenceMetrics
  }

  let modelInfo: ModelInfo | null = null
  if (modelInfoRes.status === 'fulfilled' && modelInfoRes.value.ok) {
    modelInfo = await modelInfoRes.value.json() as ModelInfo
  }

  return {
    inferenceMetrics, modelInfo, railwayApi,
    jobsLast24h: { total, complete, failed, processing,
      failureRate: total > 0 ? Math.round((failed / total) * 1000) / 10 : 0,
      avgDurationSeconds: avgDuration, recentFailed },
    checkedAt: new Date().toISOString(),
  }
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function StatusDot({ status }: { status: 'up' | 'down' | 'unknown' | 'warn' }) {
  const color = status === 'up' ? '#22C55E' : status === 'down' ? '#EF4444' : status === 'warn' ? '#F59E0B' : '#94A3B8'
  return <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: color, marginRight: 6, flexShrink: 0 }} />
}

function MetricTile({ label, value, sub, status }: { label: string; value: string | number; sub?: string; status?: 'up' | 'down' | 'warn' | 'unknown' }) {
  const c = useThemeColors()
  return (
    <div style={{ background: c.bgPrimary, border: `1px solid ${c.border}`, borderRadius: 6, padding: '14px 18px' }}>
      <div style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase' as const, color: c.textMuted, marginBottom: 6 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        {status && <StatusDot status={status} />}
        <span style={{ fontSize: 24, fontWeight: 700, color: c.textPrimary, fontFamily: '"JetBrains Mono", monospace' }}>{value}</span>
      </div>
      {sub && <div style={{ fontSize: 11, color: c.textMuted, marginTop: 4 }}>{sub}</div>}
    </div>
  )
}

function SectionHead({ title, desc }: { title: string; desc?: string }) {
  const c = useThemeColors()
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: c.textPrimary, marginBottom: 2 }}>{title}</div>
      {desc && <div style={{ fontSize: 12, color: c.textMuted }}>{desc}</div>}
    </div>
  )
}

function VramBar({ used, total }: { used: number; total: number }) {
  const c = useThemeColors()
  const pct = total > 0 ? Math.min(100, (used / total) * 100) : 0
  const color = pct > 85 ? '#EF4444' : pct > 70 ? '#F59E0B' : '#22C55E'
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 11, color: c.textMuted }}>
        <span>{used.toLocaleString()} MiB used</span>
        <span>{pct.toFixed(1)}%</span>
      </div>
      <div style={{ height: 6, background: c.border, borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 3, transition: 'width 300ms' }} />
      </div>
      <div style={{ fontSize: 10, color: c.textMuted, marginTop: 3, fontFamily: '"JetBrains Mono", monospace' }}>
        {(used / 1024).toFixed(1)} GB / {(total / 1024).toFixed(1)} GB
      </div>
    </div>
  )
}

function fmtUptime(s: number) {
  if (s < 60) return `${s}s`
  if (s < 3600) return `${Math.floor(s / 60)}m`
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60)
  return `${h}h ${m}m`
}

function fmtDuration(seconds: number) {
  if (seconds < 60) return `${Math.round(seconds)}s`
  const h = Math.floor(seconds / 3600), m = Math.floor((seconds % 3600) / 60)
  if (h > 0) return `${h}h ${m}m`
  return `${m}m`
}

// ── Latency section ───────────────────────────────────────────────────────────

function LatencySection() {
  const c = useThemeColors()
  const [stats, setStats] = useState<LatencyStats | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    internalFetch(`/v1/admin/latency-stats`)
      .then(r => r.json())
      .then(setStats)
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <div style={{ color: c.textMuted, fontSize: 12 }}>Loading latency data…</div>
  if (!stats) return null

  // Use jobs fallback if no latency_metrics yet
  const p50 = stats.p50 ?? stats.jobs.p50
  const p95 = stats.p95 ?? stats.jobs.p95
  const avg = stats.avg ?? stats.jobs.avg
  const count = stats.count > 0 ? stats.count : stats.jobs.count
  const usingFallback = stats.count === 0 && stats.jobs.count > 0

  const maxP95 = stats.timeSeries.length > 0 ? Math.max(...stats.timeSeries.map(t => t.p95 ?? 0)) : 0

  return (
    <div>
      <SectionHead title="Inference Latency — Last 7 Days" desc={usingFallback ? 'From jobs.duration_seconds (latency_metrics will populate as new jobs complete)' : `${count} samples in latency_metrics`} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 }}>
        <MetricTile label="P50 Latency" value={p50 != null ? `${p50.toFixed(1)}s` : '—'} sub="Median job duration" />
        <MetricTile label="P95 Latency" value={p95 != null ? `${p95.toFixed(1)}s` : '—'} sub="95th percentile" status={p95 != null && p95 > 120 ? 'warn' : 'up'} />
        <MetricTile label="Avg Latency" value={avg != null ? `${avg.toFixed(1)}s` : '—'} sub={`${count} samples`} />
      </div>
      {stats.timeSeries.length > 0 && (
        <div style={{ background: c.bgPrimary, border: `1px solid ${c.border}`, borderRadius: 6, padding: '14px 18px' }}>
          <div style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase' as const, color: c.textMuted, marginBottom: 12 }}>Daily P95 Latency</div>
          <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end', height: 60 }}>
            {stats.timeSeries.map(t => {
              const pct = maxP95 > 0 ? (t.p95 / maxP95) * 100 : 50
              const color = t.p95 > 120 ? '#EF4444' : t.p95 > 60 ? '#F59E0B' : '#22C55E'
              return (
                <div key={t.date} style={{ flex: 1, display: 'flex', flexDirection: 'column' as const, alignItems: 'center', gap: 4 }}>
                  <div style={{ width: '100%', height: `${Math.max(4, pct * 0.56)}px`, background: color, borderRadius: '2px 2px 0 0', minHeight: 4 }} title={`${t.date}: P95=${t.p95.toFixed(1)}s`} />
                  <div style={{ fontSize: 8, color: c.textMuted, fontFamily: '"JetBrains Mono", monospace' }}>{t.date.slice(5)}</div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

// ── Job Distribution section ──────────────────────────────────────────────────

interface PromptStat {
  prompt: string
  count: number
  avgDuration: number
}

function JobDistributionSection() {
  const c = useThemeColors()
  const [stats, setStats] = useState<PromptStat[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString()
    fetch(
      `${SUPABASE_URL}/rest/v1/jobs?select=prompt,status,duration_seconds&created_at=gte.${sevenDaysAgo}&status=eq.complete&duration_seconds=not.is.null&limit=500`,
      {
        headers: {
          apikey: SUPABASE_ANON_KEY ?? '',
          Authorization: `Bearer ${SUPABASE_ANON_KEY ?? ''}`,
        },
      }
    )
      .then(r => r.json())
      .then((rows: Array<{ prompt: string | null; duration_seconds: number | null }>) => {
        const map = new Map<string, { count: number; totalDuration: number }>()
        for (const row of rows) {
          const key = row.prompt ?? '(no prompt)'
          const existing = map.get(key) ?? { count: 0, totalDuration: 0 }
          existing.count += 1
          existing.totalDuration += row.duration_seconds ?? 0
          map.set(key, existing)
        }
        const sorted: PromptStat[] = Array.from(map.entries())
          .map(([prompt, { count, totalDuration }]) => ({
            prompt,
            count,
            avgDuration: totalDuration / count,
          }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 5)
        setStats(sorted)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <div style={{ color: c.textMuted, fontSize: 12 }}>Loading job distribution…</div>

  const maxCount = stats.length > 0 ? Math.max(...stats.map(s => s.count)) : 1

  return (
    <div>
      <SectionHead
        title="Job Distribution — Last 7 Days"
        desc={stats.length > 0 ? `Top ${stats.length} prompts by volume` : 'No completed jobs with duration in the last 7 days'}
      />
      {stats.length === 0 ? (
        <div style={{ background: c.bgPrimary, border: `1px solid ${c.border}`, borderRadius: 6, padding: '14px 18px', color: c.textMuted, fontSize: 12 }}>
          No data yet — completed jobs with duration will appear here.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 8 }}>
          {stats.map((s, i) => {
            const barPct = maxCount > 0 ? (s.count / maxCount) * 100 : 0
            const truncated = s.prompt.length > 40 ? s.prompt.slice(0, 40) + '…' : s.prompt
            return (
              <div key={i} style={{ background: c.bgPrimary, border: `1px solid ${c.border}`, borderRadius: 6, padding: '12px 16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, alignItems: 'baseline' }}>
                  <span style={{ fontSize: 12, color: c.textPrimary, fontFamily: '"JetBrains Mono", monospace', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }} title={s.prompt}>
                    {truncated}
                  </span>
                  <span style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 11, color: c.textMuted, marginLeft: 12, flexShrink: 0 }}>
                    {s.count} jobs · {s.avgDuration.toFixed(1)}s avg
                  </span>
                </div>
                <div style={{ height: 6, background: c.border, borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${barPct}%`, background: '#1B5EFF', borderRadius: 3, transition: 'width 300ms' }} />
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── Uptime section ────────────────────────────────────────────────

function UptimeSection() {
  const c = useThemeColors()
  const [stats, setStats] = useState<UptimeStats | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    internalFetch(`/v1/admin/uptime-stats`)
      .then(r => r.json())
      .then(setStats)
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <div style={{ color: c.textMuted, fontSize: 12 }}>Loading uptime data…</div>
  if (!stats) return null

  const uptime30Status: 'up' | 'down' | 'warn' = stats.uptime30d >= 99.5 ? 'up' : stats.uptime30d >= 95 ? 'warn' : 'down'
  const uptime7Status: 'up' | 'down' | 'warn' = stats.uptime7d >= 99.5 ? 'up' : stats.uptime7d >= 95 ? 'warn' : 'down'

  return (
    <div>
      <SectionHead title="Uptime / SLA" desc="Based on CloudWatch alarm ALARM→OK transitions" />
      {stats.currentAlarms.length > 0 && (
        <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, padding: '10px 14px', marginBottom: 12 }}>
          <div style={{ fontSize: 12, color: '#EF4444', fontWeight: 600 }}>🚨 Active Alarms: {stats.currentAlarms.join(', ')}</div>
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 }}>
        <MetricTile label="30-Day Uptime" value={`${stats.uptime30d.toFixed(2)}%`} status={uptime30Status} sub={stats.downtimeSeconds30d > 0 ? `${fmtDuration(stats.downtimeSeconds30d)} downtime` : 'No incidents'} />
        <MetricTile label="7-Day Uptime" value={`${stats.uptime7d.toFixed(2)}%`} status={uptime7Status} sub="Rolling 7 days" />
        <MetricTile label="Incidents (30d)" value={stats.incidents.length} status={stats.incidents.filter(i => !i.resolved_at).length > 0 ? 'warn' : 'up'} />
      </div>
      {stats.incidents.length > 0 && (
        <div style={{ border: `1px solid ${c.border}`, borderRadius: 6, overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 80px', background: c.bgPrimary, padding: '8px 16px', fontFamily: '"JetBrains Mono", monospace', fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase' as const, color: c.textMuted }}>
            <div>Alarm</div><div>Started</div><div>Resolved</div><div>Duration</div>
          </div>
          {stats.incidents.slice(0, 10).map((inc) => (
            <div key={inc.id} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 80px', padding: '10px 16px', borderTop: `1px solid ${c.border}`, alignItems: 'center' }}>
              <div style={{ fontSize: 12, color: inc.resolved_at ? c.textMuted : '#EF4444' }}>{inc.alarm_name}</div>
              <div style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 11, color: c.textMuted }}>{new Date(inc.started_at).toLocaleDateString()}</div>
              <div style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 11, color: inc.resolved_at ? c.textMuted : '#F59E0B' }}>{inc.resolved_at ? new Date(inc.resolved_at).toLocaleDateString() : 'Ongoing'}</div>
              <div style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 11, color: c.textMuted }}>{inc.duration_seconds != null ? fmtDuration(inc.duration_seconds) : '—'}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── CDN section ───────────────────────────────────────────────────────────────

function CdnSection() {
  const c = useThemeColors()
  const [metrics, setMetrics] = useState<CdnMetric[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    internalFetch(`/v1/admin/cdn-metrics`)
      .then(r => r.json())
      .then(d => setMetrics(d.metrics ?? []))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <div style={{ color: c.textMuted, fontSize: 12 }}>Loading CDN metrics…</div>

  const latest = metrics[0]

  return (
    <div>
      <SectionHead title="CDN / CloudFront Metrics" desc="Daily aggregated from CloudFront access logs. Enable logging in CloudFront console → S3 bucket: primate-vision-results/cf-logs/" />
      {metrics.length === 0 ? (
        <div style={{ background: c.bgPrimary, border: `1px solid ${c.border}`, borderRadius: 6, padding: '14px 18px', color: c.textMuted, fontSize: 12 }}>
          No CDN metrics yet. CloudFront access logging needs to be enabled and the daily parse cron needs to run.
          <br /><br />
          <strong style={{ color: '#F59E0B' }}>To enable:</strong> Go to CloudFront console → Distribution d3silto12vjvss → Logging → Enable → S3 bucket: primate-vision-results.s3.amazonaws.com → Prefix: cf-logs/
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
          <MetricTile label="Cache Hit Rate" value={latest ? `${latest.cache_hit_rate.toFixed(1)}%` : '—'} status={latest && latest.cache_hit_rate > 80 ? 'up' : 'warn'} sub={latest?.date ?? ''} />
          <MetricTile label="Total Requests" value={latest ? latest.total_requests.toLocaleString() : '—'} sub="Yesterday" />
          <MetricTile label="4xx Errors" value={latest ? latest.error_4xx_count : '—'} status={latest && latest.error_4xx_count > 100 ? 'warn' : 'up'} />
          <MetricTile label="5xx Errors" value={latest ? latest.error_5xx_count : '—'} status={latest && latest.error_5xx_count > 0 ? 'down' : 'up'} />
        </div>
      )}
    </div>
  )
}

// ── API Latency section ───────────────────────────────────────────────────────

function ApiLatencySection() {
  const c = useThemeColors()
  const [data, setData] = useState<{ routes: ApiLatencyRoute[]; total: number } | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    internalFetch(`/v1/admin/api-latency`)
      .then(r => r.json())
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <div style={{ color: c.textMuted, fontSize: 12 }}>Loading API latency…</div>
  if (!data || data.routes.length === 0) {
    return (
      <div>
        <SectionHead title="Railway API Latency — Last 24h" desc="Per-route P50/P95 from api_request_metrics" />
        <div style={{ background: c.bgPrimary, border: `1px solid ${c.border}`, borderRadius: 6, padding: '14px 18px', color: c.textMuted, fontSize: 12 }}>
          No API metrics yet. Data will populate as requests come in (tracked via onResponse hook for /v1/video, /v1/user-videos, /v1/model-info routes).
        </div>
      </div>
    )
  }

  return (
    <div>
      <SectionHead title="Railway API Latency — Last 24h" desc={`${data.total} requests tracked`} />
      <div style={{ border: `1px solid ${c.border}`, borderRadius: 6, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 80px 80px 80px 60px 80px', background: c.bgPrimary, padding: '8px 16px', fontFamily: '"JetBrains Mono", monospace', fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase' as const, color: c.textMuted }}>
          <div>Route</div><div>P50</div><div>P95</div><div>Avg</div><div>Count</div><div>Error Rate</div>
        </div>
        {data.routes.map((r, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 80px 80px 80px 60px 80px', padding: '10px 16px', borderTop: `1px solid ${c.border}`, alignItems: 'center' }}>
            <code style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 11, color: c.textPrimary }}>{r.route}</code>
            <div style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 11, color: c.textMuted }}>{r.p50 != null ? `${r.p50}ms` : '—'}</div>
            <div style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 11, color: r.p95 != null && r.p95 > 2000 ? '#F59E0B' : c.textMuted }}>{r.p95 != null ? `${r.p95}ms` : '—'}</div>
            <div style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 11, color: c.textMuted }}>{r.avg != null ? `${Math.round(r.avg)}ms` : '—'}</div>
            <div style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 11, color: c.textMuted }}>{r.count}</div>
            <div style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 11, color: r.errorRate > 5 ? '#EF4444' : '#22C55E' }}>{r.errorRate}%</div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Cost section ──────────────────────────────────────────────────────────────

function CostSection() {
  const c = useThemeColors()
  const [stats, setStats] = useState<CostStats | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    internalFetch(`/v1/admin/cost-stats`)
      .then(r => r.json())
      .then(setStats)
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <div style={{ color: c.textMuted, fontSize: 12 }}>Loading cost data…</div>
  if (!stats) return null

  if (!stats.ceAccessible) {
    return (
      <div>
        <SectionHead title="AWS Cost Tracking" />
        <div style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 6, padding: '14px 18px' }}>
          <div style={{ fontSize: 12, color: '#F59E0B', fontWeight: 600, marginBottom: 6 }}>⚠ Cost tracking requires ce:GetCostAndUsage IAM permission</div>
          <div style={{ fontSize: 11, color: c.textMuted }}>
            The IAM user (primate-inference-s3) needs the following policy added:<br />
            <code style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 11, color: c.textPrimary, background: c.bgPrimary, padding: '2px 6px', borderRadius: 3 }}>{"{ \"Effect\": \"Allow\", \"Action\": \"ce:GetCostAndUsage\", \"Resource\": \"*\" }"}</code>
          </div>
        </div>
      </div>
    )
  }

  const services = stats.liveData ?? []
  const maxCost = services.length > 0 ? Math.max(...services.map(s => s.cost)) : 0

  return (
    <div>
      <SectionHead title="AWS Cost — Current Month" desc={`Total: $${stats.total.toFixed(2)}`} />
      <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 6 }}>
        {services.sort((a, b) => b.cost - a.cost).map((s, i) => (
          <div key={i} style={{ background: c.bgPrimary, border: `1px solid ${c.border}`, borderRadius: 4, padding: '10px 14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: 12, color: c.textPrimary }}>{s.service}</span>
              <span style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 12, color: c.textPrimary }}>${s.cost.toFixed(2)}</span>
            </div>
            <div style={{ height: 4, background: c.border, borderRadius: 2, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${maxCost > 0 ? (s.cost / maxCost) * 100 : 0}%`, background: c.signal, borderRadius: 2 }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Queue Health ──────────────────────────────────────────────────────────────

function generateMockQueueHealth(): QueueHealthData {
  const dateStr = new Date().toISOString().slice(0, 10)
  const s = dateStr.split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0)
  const pseudo = (i: number) => ((s * 31 + i * 17) % 100) / 100

  const now = new Date()
  const timeSeries: Array<{ hour: string; stream: number; file: number }> = []
  for (let i = 23; i >= 0; i--) {
    const d = new Date(now.getTime() - i * 3600 * 1000)
    const hour = `${d.getHours().toString().padStart(2, '0')}:00`
    const stream = Math.floor(pseudo(i * 2) * 6)
    const file = Math.floor(pseudo(i * 3 + 1) * 5)
    timeSeries.push({ hour, stream, file })
  }

  const peakStream = Math.max(...timeSeries.map(t => t.stream))
  const peakFile = Math.max(...timeSeries.map(t => t.file))
  const busyHours = timeSeries.filter(t => t.stream > 0 || t.file > 0).length
  const utilizationPct = Math.round((busyHours / 24) * 100)
  const queueFullSessions = Math.floor(pseudo(50) * 4)

  return { peakStreamDepth: peakStream, peakFileDepth: peakFile, utilizationPct, queueFullSessions, timeSeries }
}

function QueueTooltip({ active, payload, label }: { active?: boolean; payload?: Array<{ name: string; value: number; color: string }>; label?: string }) {
  const c = useThemeColors()
  if (!active || !payload?.length) return null
  return (
    <div style={{ background: c.bgSecondary, border: `1px solid ${c.border}`, borderRadius: 4, padding: '8px 12px', fontSize: 11, fontFamily: '"JetBrains Mono", monospace' }}>
      <div style={{ color: c.textMuted, marginBottom: 4 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color }}>
          {p.name}: {p.value}
        </div>
      ))}
    </div>
  )
}

function QueueHealthTab() {
  const c = useThemeColors()
  const [data, setData] = useState<QueueHealthData | null>(null)
  const [isMock, setIsMock] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    internalFetch(`/v1/admin/analytics/queue-health?period=24h`)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.json() as Promise<QueueHealthData>
      })
      .then(d => { setData(d); setIsMock(false) })
      .catch(() => { setData(generateMockQueueHealth()); setIsMock(true) })
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <div style={{ color: c.textMuted, fontSize: 12 }}>Loading queue health…</div>
  if (!data) return null

  const peakMax = Math.max(data.peakStreamDepth, data.peakFileDepth)
  const scalingColor = peakMax > 7 ? '#EF4444' : peakMax >= 3 ? '#F59E0B' : '#22C55E'
  const scalingBg = peakMax > 7 ? 'rgba(239,68,68,0.08)' : peakMax >= 3 ? 'rgba(245,158,11,0.08)' : 'rgba(34,197,94,0.08)'
  const scalingBorder = peakMax > 7 ? 'rgba(239,68,68,0.3)' : peakMax >= 3 ? 'rgba(245,158,11,0.3)' : 'rgba(34,197,94,0.25)'
  const scalingLabel = peakMax > 7
    ? 'Scale now — users waiting 5+ minutes'
    : peakMax >= 3
    ? 'Monitor — consider adding capacity'
    : 'No scaling needed'

  return (
    <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 28 }}>
      {isMock && (
        <div style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 6, padding: '8px 14px', fontSize: 11, color: '#F59E0B', fontFamily: '"JetBrains Mono", monospace' }}>
          No data yet — showing placeholder
        </div>
      )}

      {/* Section 1 — Metric cards */}
      <div>
        <SectionHead title="Queue Metrics — Last 24h" />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
          <MetricTile label="Peak Stream Queue Depth" value={data.peakStreamDepth} sub="Max concurrent" />
          <MetricTile label="Peak File Queue Depth" value={data.peakFileDepth} sub="Max concurrent" />
          <MetricTile
            label="% Time Queue Depth > 0"
            value={`${data.utilizationPct}%`}
            sub="Utilization"
            status={data.utilizationPct > 80 ? 'warn' : 'up'}
          />
          <MetricTile
            label="Queue Full Sessions"
            value={data.queueFullSessions}
            sub="Hit capacity limit"
            status={data.queueFullSessions > 0 ? 'warn' : 'up'}
          />
        </div>
      </div>

      {/* Section 2 — Scaling indicator */}
      <div style={{ background: scalingBg, border: `1px solid ${scalingBorder}`, borderRadius: 6, padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: scalingColor, flexShrink: 0 }} />
        <span style={{ fontSize: 13, fontWeight: 600, color: scalingColor, fontFamily: '"JetBrains Mono", monospace' }}>
          {scalingLabel}
        </span>
        <span style={{ fontSize: 11, color: c.textMuted, marginLeft: 8 }}>
          peak depth: {peakMax} (stream: {data.peakStreamDepth}, file: {data.peakFileDepth})
        </span>
      </div>

      {/* Section 3 — Queue Depth Over Time */}
      <div>
        <SectionHead title="Queue Depth Over Time" desc="Hourly stream and file queue depths over the last 24h" />
        <div style={{ background: c.bgPrimary, border: `1px solid ${c.border}`, borderRadius: 6, padding: '16px 18px' }}>
          <div style={{ display: 'flex', gap: 20, marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: c.textMuted, fontFamily: '"JetBrains Mono", monospace' }}>
              <div style={{ width: 24, height: 2, background: c.signal, borderRadius: 1 }} />
              stream
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: c.textMuted, fontFamily: '"JetBrains Mono", monospace' }}>
              <div style={{ width: 24, height: 2, background: c.aurora, borderRadius: 1 }} />
              file
            </div>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={data.timeSeries} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={c.border} />
              <XAxis
                dataKey="hour"
                stroke={c.border}
                tick={{ fill: c.textMuted, fontSize: 10, fontFamily: '"JetBrains Mono", monospace' }}
                tickLine={false}
                interval={3}
              />
              <YAxis
                stroke={c.border}
                tick={{ fill: c.textMuted, fontSize: 10, fontFamily: '"JetBrains Mono", monospace' }}
                tickLine={false}
                axisLine={false}
                allowDecimals={false}
              />
              <Tooltip content={<QueueTooltip />} />
              <Line type="monotone" dataKey="stream" stroke={c.signal} strokeWidth={2} dot={false} name="stream" />
              <Line type="monotone" dataKey="file" stroke={c.aurora} strokeWidth={2} dot={false} name="file" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  )
}

// ── Tabs ───────────────────────────────────────────────────────────────────────

function OverviewTab({ status, loading }: { status: SystemStatus | null; loading: boolean }) {
  const c = useThemeColors()
  if (loading) return <div style={{ color: c.textMuted, padding: 32, textAlign: 'center' as const }}>Loading system status…</div>
  if (!status) return <div style={{ color: '#EF4444', padding: 32 }}>Failed to load system status.</div>

  const { jobsLast24h: jobs, railwayApi, inferenceMetrics: im, modelInfo } = status
  const inferenceStatus: 'up' | 'down' | 'unknown' = im ? (im.model_loaded ? 'up' : 'down') : 'unknown'

  return (
    <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 28 }}>
      {/* System health */}
      <div>
        <SectionHead title="System Health" desc="Current status of all major components" />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
          <MetricTile label="Railway API" value={railwayApi.toUpperCase()} status={railwayApi} />
          <MetricTile
            label="EC2 Inference"
            value={im ? (im.model_loaded ? 'UP' : 'LOADING') : 'UNKNOWN'}
            status={inferenceStatus}
            sub={im ? `Uptime ${fmtUptime(im.uptime_seconds)}` : 'Cannot reach via API'}
          />
          <MetricTile label="Supabase DB" value="UP" status="up" sub="Queries returning" />
          <MetricTile label="CloudFront CDN" value="UP" status="up" />
        </div>
      </div>

      {/* GPU metrics */}
      {im && (
        <div>
          <SectionHead title={`GPU — NVIDIA L40S (${(im.gpu_vram_total_mb / 1024).toFixed(0)}GB VRAM)`} desc="Real-time from inference server /v1/metrics" />
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 12 }}>
            <div style={{ background: c.bgPrimary, border: `1px solid ${c.border}`, borderRadius: 6, padding: '14px 18px' }}>
              <div style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase' as const, color: c.textMuted, marginBottom: 10 }}>VRAM Usage</div>
              <VramBar used={im.gpu_vram_used_mb} total={im.gpu_vram_total_mb} />
            </div>
            <MetricTile label="Queue Depth" value={im.queue_depth} status={im.queue_depth > 8 ? 'warn' : 'up'} sub="Jobs waiting" />
            <MetricTile label="Jobs Processed" value={im.jobs_processed_total} sub="Since last restart" />
            <MetricTile label="Jobs Failed" value={im.jobs_failed_total} status={im.jobs_failed_total > 0 ? 'warn' : 'up'} sub="Since last restart" />
          </div>
          {modelInfo && (
            <div style={{ marginTop: 12, background: c.bgPrimary, border: `1px solid ${c.border}`, borderRadius: 6, padding: '12px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase' as const, color: c.textMuted, marginBottom: 4 }}>Active Model</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: c.textPrimary }}>{modelInfo.model_name}</div>
              </div>
              <div style={{ textAlign: 'right' as const }}>
                <div style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase' as const, color: c.textMuted, marginBottom: 4 }}>Checkpoints</div>
                <code style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 12, color: c.textMuted }}>
                  VL step {modelInfo.vl_step?.toLocaleString() ?? '—'} · JEPA step {modelInfo.jepa_step?.toLocaleString() ?? '—'}
                </code>
              </div>
            </div>
          )}
        </div>
      )}
      {!im && (
        <div style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 6, padding: '12px 18px' }}>
          <div style={{ fontSize: 12, color: '#EF4444' }}>⚠ Could not reach inference server /v1/metrics — GPU data unavailable</div>
        </div>
      )}

      {/* Jobs last 24h */}
      <div>
        <SectionHead title="Inference Jobs — Last 24h" />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12 }}>
          <MetricTile label="Total Jobs" value={jobs.total} />
          <MetricTile label="Completed" value={jobs.complete} status={jobs.complete === jobs.total && jobs.total > 0 ? 'up' : 'unknown'} />
          <MetricTile label="Failed" value={jobs.failed} status={jobs.failed > 0 ? (jobs.failureRate > 10 ? 'down' : 'warn') : 'up'} />
          <MetricTile label="Processing" value={jobs.processing} status={jobs.processing > 5 ? 'warn' : 'up'} />
          <MetricTile label="Failure Rate" value={`${jobs.failureRate}%`} status={jobs.failureRate > 10 ? 'down' : jobs.failureRate > 0 ? 'warn' : 'up'} />
        </div>
        {jobs.avgDurationSeconds != null && (
          <div style={{ marginTop: 8, fontSize: 12, color: c.textMuted }}>Avg job duration: <span style={{ color: c.textPrimary, fontFamily: '"JetBrains Mono", monospace' }}>{jobs.avgDurationSeconds.toFixed(1)}s</span></div>
        )}
      </div>

      {/* Latency P50/P95 */}
      <LatencySection />

      {/* Job distribution */}
      <JobDistributionSection />

      {/* CDN metrics */}
      <CdnSection />

      {/* API Latency */}
      <ApiLatencySection />

      {/* Uptime */}
      <UptimeSection />

      {/* Cost */}
      <CostSection />

      {/* Recent failures */}
      {jobs.recentFailed.length > 0 && (
        <div>
          <SectionHead title="Recent Job Failures" />
          <div style={{ border: `1px solid ${c.border}`, borderRadius: 6, overflow: 'hidden' }}>
            {jobs.recentFailed.map((f, i) => (
              <div key={i} style={{ padding: '10px 16px', borderBottom: i < jobs.recentFailed.length - 1 ? `1px solid ${c.border}` : 'none', display: 'flex', gap: 12 }}>
                <div style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 10, color: c.textMuted, flexShrink: 0, marginTop: 1 }}>{f.created_at.slice(11, 19)}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, color: c.textPrimary, marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{f.prompt}</div>
                  <div style={{ fontSize: 11, color: '#EF4444', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{f.error_message ?? 'No error message'}</div>
                </div>
                <div style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 9, color: c.textMuted, flexShrink: 0 }}>{f.id.slice(0, 8)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ fontSize: 11, color: c.textMuted, textAlign: 'right' as const }}>
        Last checked: {new Date(status.checkedAt).toLocaleTimeString()}
      </div>
    </div>
  )
}

interface BlueGreenStatus {
  activeCheckpoint: string
  refresh: {
    id: string
    status: string
    percentageComplete: number
    startedAt: string
  } | null
}

function ModelTab() {
  const c = useThemeColors()
  const [modelInfo, setModelInfo] = useState<ModelInfo | null>(null)
  const [checkpoints, setCheckpoints] = useState<CheckpointInfo[]>([])
  const [loading, setLoading] = useState(true)
  const [switching, setSwitching] = useState<string | null>(null)  // for local reload path
  const [deploying, setDeploying] = useState<string | null>(null)  // for blue/green path
  const [statusMsg, setStatusMsg] = useState<string | null>(null)
  const [deployProgress, setDeployProgress] = useState<BlueGreenStatus | null>(null)
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const refreshModelInfo = async () => {
    try {
      // PRI (2026-06-26): public /v1/model-info no longer returns checkpoint
      // detail. Use the admin-gated proxy with the Clerk bearer token.
      const token = await getAuthToken()
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : undefined
      const [mi, cp] = await Promise.allSettled([
        internalFetch(`/v1/model-info-proxy`, { headers: authHeaders }),
        internalFetch(`/v1/admin/checkpoints`, { headers: authHeaders }),
      ])
      if (mi.status === 'fulfilled' && mi.value.ok) setModelInfo(await mi.value.json())
      if (cp.status === 'fulfilled' && cp.value.ok) {
        const data = await cp.value.json() as { checkpoints: CheckpointInfo[] }
        setCheckpoints(data.checkpoints ?? [])
      }
    } catch {}
  }

  useEffect(() => {
    refreshModelInfo().finally(() => setLoading(false))
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current)
    }
  }, [])

  // Poll blue-green status every 30s while a deploy is in progress
  const startPolling = () => {
    if (pollTimerRef.current) clearInterval(pollTimerRef.current)
    pollTimerRef.current = setInterval(async () => {
      try {
        const res = await internalFetch(`/v1/admin/blue-green-status`)
        if (res.ok) {
          const data = await res.json() as BlueGreenStatus
          setDeployProgress(data)
          const status = data.refresh?.status
          if (status === 'Successful') {
            clearInterval(pollTimerRef.current!)
            pollTimerRef.current = null
            setDeploying(null)
            setStatusMsg(`✅ Deploy complete! Active checkpoint: ${data.activeCheckpoint}`)
            await refreshModelInfo()
          } else if (status === 'Failed' || status === 'Cancelled') {
            clearInterval(pollTimerRef.current!)
            pollTimerRef.current = null
            setDeploying(null)
            setStatusMsg(`❌ Deploy ${status?.toLowerCase()}. Check AWS console for details.`)
          }
        }
      } catch {}
    }, 30000)
  }

  // LOCAL path: checkpoint already downloaded, fast reload (~4 min, same instance)
  const switchCheckpoint = async (name: string) => {
    if (!confirm(`⚠️ EMERGENCY ONLY: In-place reload of "${name}"\n\nThis causes ~4 minutes of DOWNTIME for all users.\nThe current server will stop and restart with new weights.\n\nNormal deploys use the zero-downtime "Deploy" button instead.\n\nAre you sure you want to force an in-place reload?`)) return
    setSwitching(name)
    setStatusMsg(null)
    try {
      const res = await internalFetch(`/v1/admin/reload-checkpoint`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vl_checkpoint: name }),
      })
      const data = await res.json() as { ok?: boolean; message?: string; error?: string }
      setStatusMsg(data.ok ? `✅ ${data.message}` : `❌ ${data.error}`)
      if (data.ok) await refreshModelInfo()
    } catch (e) {
      setStatusMsg(`❌ ${String(e)}`)
    } finally {
      setSwitching(null)
    }
  }

  // BLUE/GREEN path: S3-only checkpoint, zero-downtime (~15-20 min, new instance)
  const blueGreenDeploy = async (name: string) => {
    if (!confirm(`Deploy "${name}" via zero-downtime blue/green?\n\nA new instance will start, download from S3, and replace the current one.\nETA: ~15-20 minutes.`)) return
    setDeploying(name)
    setStatusMsg(null)
    setDeployProgress(null)
    try {
      const res = await internalFetch(`/v1/admin/blue-green-deploy`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vl_checkpoint: name }),
      })
      const data = await res.json() as { ok?: boolean; checkpoint?: string; refreshId?: string; message?: string; error?: string }
      if (data.ok) {
        setStatusMsg(`🚀 Deploying ${name}… New instance starting. ETA ~15-20 min. Refresh ID: ${data.refreshId}`)
        startPolling()
      } else {
        setDeploying(null)
        setStatusMsg(`❌ Deploy failed: ${data.error}`)
      }
    } catch (e) {
      setDeploying(null)
      setStatusMsg(`❌ ${String(e)}`)
    }
  }

  if (loading) return <div style={{ color: c.textMuted, padding: 32, textAlign: 'center' as const }}>Loading model info…</div>

  const isDeployInProgress = deploying !== null

  return (
    <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 24 }}>
      {statusMsg && (
        <div style={{ background: statusMsg.startsWith('✅') ? 'rgba(34,197,94,0.08)' : statusMsg.startsWith('🚀') ? 'rgba(32,88,240,0.08)' : 'rgba(239,68,68,0.08)', border: `1px solid ${statusMsg.startsWith('✅') ? 'rgba(34,197,94,0.2)' : statusMsg.startsWith('🚀') ? 'rgba(32,88,240,0.3)' : 'rgba(239,68,68,0.2)'}`, borderRadius: 6, padding: '12px 16px', fontSize: 13, color: statusMsg.startsWith('✅') ? '#22C55E' : statusMsg.startsWith('🚀') ? c.signal : '#EF4444' }}>
          {statusMsg}
          {deployProgress?.refresh && !statusMsg.startsWith('✅') && !statusMsg.startsWith('❌') && (
            <div style={{ marginTop: 8, fontSize: 12, color: c.textMuted }}>
              ASG refresh status: <strong style={{ color: c.textPrimary }}>{deployProgress.refresh.status}</strong>
              {deployProgress.refresh.percentageComplete != null && ` (${deployProgress.refresh.percentageComplete}% complete)`}
            </div>
          )}
        </div>
      )}

      {/* Current model */}
      <div style={{ background: c.bgPrimary, border: `1px solid ${c.border}`, borderRadius: 8, padding: '20px 24px' }}>
        <SectionHead title="Active Model" />
        {modelInfo ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
            {[
              { label: 'Model Name', value: modelInfo.model_name },
              { label: 'VL Checkpoint', value: `${modelInfo.vl_checkpoint} (step ${modelInfo.vl_step?.toLocaleString() ?? '?'})` },
              { label: 'JEPA Checkpoint', value: `${modelInfo.jepa_checkpoint} (step ${modelInfo.jepa_step?.toLocaleString() ?? '?'})` },
            ].map(item => (
              <div key={item.label}>
                <div style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase' as const, color: c.textMuted, marginBottom: 6 }}>{item.label}</div>
                <code style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 13, color: c.textPrimary }}>{item.value}</code>
              </div>
            ))}
          </div>
        ) : (
          <p style={{ color: c.textMuted, fontSize: 13 }}>Could not reach inference server — model info unavailable.</p>
        )}
      </div>

      {/* Available checkpoints */}
      <div>
        <SectionHead title="Available Checkpoints" desc="Local = already downloaded (fast reload, ~4 min). S3-only = zero-downtime deploy via new instance (~15-20 min)." />
        {checkpoints.length === 0 ? (
          <div style={{ background: c.bgPrimary, border: `1px solid ${c.border}`, borderRadius: 6, padding: '16px 18px', color: c.textMuted, fontSize: 13 }}>
            No checkpoints found. If checkpoints are on S3, they will be listed here too.
          </div>
        ) : (
          <div style={{ border: `1px solid ${c.border}`, borderRadius: 6, overflow: 'hidden' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 80px 80px 160px', background: c.bgPrimary, padding: '8px 16px', fontFamily: '"JetBrains Mono", monospace', fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase' as const, color: c.textMuted }}>
              <div>Filename</div><div>Step</div><div>Size</div><div>Location</div><div></div>
            </div>
            {checkpoints.map((cp, i) => {
              const isActive = modelInfo?.vl_checkpoint === cp.name
              const isLocal = cp.size_mb > 0  // local checkpoints have a size; S3-only entries have size 0 or are marked
              const isBusy = switching === cp.name || deploying === cp.name
              return (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 80px 80px 80px 160px', padding: '12px 16px', borderTop: `1px solid ${c.border}`, alignItems: 'center', background: isActive ? 'rgba(32,88,240,0.06)' : 'transparent' }}>
                  <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 2 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <code style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 12, color: c.textPrimary }}>{cp.name}</code>
                      {isActive && <span style={{ fontSize: 9, fontWeight: 700, color: c.signal, background: 'rgba(32,88,240,0.15)', padding: '2px 6px', borderRadius: 3, letterSpacing: '0.06em' }}>ACTIVE</span>}
                    </div>
                    {!isLocal && <div style={{ fontSize: 10, color: c.textMuted }}>Will download from S3 (~5-10 min) then reload (~4 min)</div>}
                  </div>
                  <div style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 12, color: c.textMuted }}>{cp.step.toLocaleString()}</div>
                  <div style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 12, color: c.textMuted }}>{cp.size_mb > 0 ? `${cp.size_mb.toFixed(0)} MB` : '—'}</div>
                  <div style={{ fontSize: 11, color: isLocal ? '#22C55E' : '#F59E0B' }}>{isLocal ? 'Local' : 'S3 only'}</div>
                  <div style={{ display: 'flex', flexDirection: 'column' as const, alignItems: 'flex-end', gap: 4 }}>
                    {!isActive && (
                      <button
                        onClick={() => blueGreenDeploy(cp.name)}
                        disabled={isDeployInProgress}
                        style={{ padding: '6px 14px', background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 4, color: '#22C55E', fontSize: 12, fontWeight: 600, cursor: isDeployInProgress ? 'not-allowed' : 'pointer', opacity: isDeployInProgress ? 0.5 : 1 }}
                      >
                        {isBusy ? 'Deploying…' : 'Deploy'}
                      </button>
                    )}
                    {!isActive && isLocal && (
                      <button
                        onClick={() => switchCheckpoint(cp.name)}
                        disabled={!!switching || isDeployInProgress}
                        style={{ background: 'none', border: 'none', cursor: (switching || isDeployInProgress) ? 'not-allowed' : 'pointer', color: c.textMuted, fontSize: 10, padding: '0 2px', opacity: (switching || isDeployInProgress) ? 0.4 : 0.7, textDecoration: 'underline', letterSpacing: '0.02em' }}
                        title="Emergency only — causes ~4 min downtime on current server"
                      >
                        {isBusy ? 'Reloading…' : '⚠ Emergency reload'}
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      <div style={{ fontSize: 12, color: c.textMuted, background: 'rgba(34,197,94,0.04)', border: '1px solid rgba(34,197,94,0.15)', borderRadius: 6, padding: '10px 14px' }}>
        <strong style={{ color: '#22C55E' }}>Deploy</strong> — Zero-downtime. Starts a new server with the new checkpoint while the current one keeps running. ETA ~15-20 min.<br/>
        <span style={{ color: c.textMuted, fontSize: 11 }}>⚠ Emergency reload (small link) — Restarts the current server in-place. Causes ~4 min downtime. Only use if the server is already down or broken.</span>
      </div>
    </div>
  )
}

// ── Logs Tab ───────────────────────────────────────────────────────────────────

function LogsTab() {
  const c = useThemeColors()
  const [events, setEvents] = useState<LogEvent[]>([])
  const [filter, setFilter] = useState('')
  const [loading, setLoading] = useState(false)
  const [autoScroll, setAutoScroll] = useState(true)
  const [logGroupExists, setLogGroupExists] = useState<boolean | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const logRef = useRef<HTMLDivElement>(null)

  const fetchLogs = useCallback(async (filterStr = filter, limit = 100) => {
    setLoading(true)
    setError(null)
    try {
      const res = await internalFetch(`/v1/admin/logs?limit=${limit}&filter=${encodeURIComponent(filterStr)}`)
      const data = await res.json() as { events: LogEvent[]; logGroupExists?: boolean; error?: string; note?: string }
      setEvents(data.events ?? [])
      setLogGroupExists(data.logGroupExists ?? null)
      setError(data.error ?? null)
      setNote(data.note ?? null)
    } catch (e) {
      setError(String(e))
    } finally {
      setLoading(false)
    }
  }, [filter])

  useEffect(() => { fetchLogs() }, [])

  useEffect(() => {
    if (autoScroll && logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight
    }
  }, [events, autoScroll])

  const fmtTs = (ts: number) => new Date(ts).toISOString().replace('T', ' ').slice(0, 23)

  return (
    <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 16 }}>
      <SectionHead title="Inference Server Logs" desc="CloudWatch Logs from /primate/inference log group — last 24h" />

      {logGroupExists === false && (
        <div style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 6, padding: '14px 18px' }}>
          <div style={{ fontSize: 13, color: '#F59E0B', fontWeight: 600, marginBottom: 8 }}>⚠ CloudWatch Logs Agent not configured</div>
          <div style={{ fontSize: 12, color: c.textMuted, lineHeight: 1.7 }}>
            The log group <code style={{ fontFamily: '"JetBrains Mono", monospace', color: c.textPrimary }}>/primate/inference</code> doesn't exist yet. To enable log shipping from the inference server:
          </div>
          <pre style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 11, color: c.textMuted, background: c.bgPrimary, padding: '10px 12px', borderRadius: 4, marginTop: 10, overflow: 'auto' }}>{`# Access inference server via SSM (no SSH key needed):
# Resolve the live ASG instance id first (it cycles, never hardcode):
LIVE_ID=$(aws --profile primate-inference-ssm --region us-west-2 ec2 describe-instances \
  --filters Name=tag:Name,Values=primate-inference-asg Name=instance-state-name,Values=running \
  --query 'Reservations[0].Instances[0].InstanceId' --output text)
aws --profile primate-inference-ssm ssm start-session --target "$LIVE_ID" --region us-west-2

# Create CW agent config:
sudo tee /opt/aws/amazon-cloudwatch-agent/etc/amazon-cloudwatch-agent.json << 'EOF'
{
  "logs": {
    "logs_collected": {
      "files": {
        "collect_list": [{
          "file_path": "/home/ubuntu/primate/logs/inference.log",
          "log_group_name": "/primate/inference",
          "log_stream_name": "{instance_id}",
          "timezone": "UTC"
        }]
      }
    }
  }
}
EOF

# Start the agent:
sudo /opt/aws/amazon-cloudwatch-agent/bin/amazon-cloudwatch-agent-ctl \\
  -a fetch-config -m ec2 -s \\
  -c file:/opt/aws/amazon-cloudwatch-agent/etc/amazon-cloudwatch-agent.json
sudo systemctl enable amazon-cloudwatch-agent
sudo systemctl start amazon-cloudwatch-agent`}</pre>
        </div>
      )}

      {/* Controls */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <input
          value={filter}
          onChange={e => setFilter(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && fetchLogs(filter)}
          placeholder="Filter logs (e.g. ERROR, job_id:)"
          style={{ flex: 1, padding: '8px 12px', background: c.bgPrimary, border: `1px solid ${c.border}`, borderRadius: 4, color: c.textPrimary, fontSize: 12, fontFamily: '"JetBrains Mono", monospace', outline: 'none' }}
        />
        <button onClick={() => fetchLogs(filter)} disabled={loading} style={{ padding: '8px 16px', background: 'rgba(32,88,240,0.1)', border: '1px solid rgba(32,88,240,0.3)', borderRadius: 4, color: c.signal, fontSize: 12, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.5 : 1 }}>
          {loading ? 'Loading…' : '↻ Refresh'}
        </button>
        <button onClick={() => fetchLogs(filter, 500)} disabled={loading} style={{ padding: '8px 14px', background: 'transparent', border: `1px solid ${c.border}`, borderRadius: 4, color: c.textMuted, fontSize: 12, cursor: loading ? 'not-allowed' : 'pointer' }}>
          Load 500
        </button>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: c.textMuted, cursor: 'pointer' }}>
          <input type="checkbox" checked={autoScroll} onChange={e => setAutoScroll(e.target.checked)} />
          Auto-scroll
        </label>
      </div>

      {error && !note && (
        <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 4, padding: '10px 14px', fontSize: 12, color: '#EF4444' }}>
          {error}
        </div>
      )}

      {/* Log viewer */}
      <div
        ref={logRef}
        style={{ background: c.bgPrimary, border: `1px solid ${c.border}`, borderRadius: 6, padding: '12px', height: 480, overflowY: 'auto', fontFamily: '"JetBrains Mono", monospace', fontSize: 11, lineHeight: 1.6 }}
      >
        {events.length === 0 ? (
          <div style={{ color: c.textMuted, textAlign: 'center' as const, marginTop: 40 }}>
            {loading ? 'Loading logs…' : logGroupExists === false ? 'No log group found. Configure CloudWatch agent (see instructions above).' : 'No log events found in the last 24h'}
          </div>
        ) : (
          events.map((e, i) => (
            <div key={i} style={{ display: 'flex', gap: 12, padding: '1px 0', borderBottom: `1px solid ${c.bgPrimary}` }}>
              <span style={{ color: c.textMuted, flexShrink: 0 }}>{fmtTs(e.timestamp!)}</span>
              <span style={{ color: e.message?.includes('ERROR') || e.message?.includes('error') ? '#EF4444' : e.message?.includes('WARN') ? '#F59E0B' : c.textMuted, wordBreak: 'break-all' as const }}>
                {e.message}
              </span>
            </div>
          ))
        )}
      </div>
      <div style={{ fontSize: 11, color: c.textMuted, display: 'flex', justifyContent: 'space-between' }}>
        <span>{events.length} events</span>
        {note && <span style={{ color: '#F59E0B' }}>⚠ {note}</span>}
      </div>
    </div>
  )
}

// ── Main page ──────────────────────────────────────────────────────────────────

type DevOpsTab = 'overview' | 'model' | 'logs' | 'queue'

const DEVOPS_TABS: Array<{ id: DevOpsTab; label: string }> = [
  { id: 'overview', label: 'System Overview' },
  { id: 'model', label: 'Model & Checkpoints' },
  { id: 'logs', label: 'Logs' },
  { id: 'queue', label: 'Queue Health' },
]

export default function AdminDevOps() {
  const { user, isLoaded } = useUser()
  const c = useThemeColors()
  const [tab, setTab] = useState<DevOpsTab>('overview')
  const [status, setStatus] = useState<SystemStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const email = user?.primaryEmailAddress?.emailAddress ?? user?.emailAddresses?.[0]?.emailAddress ?? ''
  const isAdmin = isLoaded && !!user && ADMIN_EMAILS.includes(email)

  const refresh = useCallback(() => {
    setLoading(true)
    setError(null)
    fetchSystemStatus()
      .then(setStatus)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { if (isAdmin) refresh() }, [isAdmin, refresh])

  if (!isLoaded) return <div style={{ minHeight: '100vh', background: c.bgPrimary }} />
  if (!isAdmin) return <div style={{ minHeight: '100vh', background: c.bgPrimary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><p style={{ color: c.textMuted }}>Access denied</p></div>

  return (
    <div style={{ flex: 1, padding: '32px', color: c.textPrimary, minHeight: '100vh', background: c.bgPrimary }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
        <div>
          <div style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase' as const, color: c.signal, marginBottom: 4 }}>Internal</div>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: c.textPrimary, letterSpacing: '-0.02em' }}>DevOps</h1>
          <p style={{ fontSize: 13, color: c.textMuted, marginTop: 2 }}>System health, infrastructure metrics, and observability roadmap</p>
        </div>
        <button onClick={refresh} disabled={loading} style={{ padding: '8px 16px', background: 'rgba(32,88,240,0.1)', border: '1px solid rgba(32,88,240,0.3)', borderRadius: 4, color: c.signal, fontSize: 12, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.5 : 1 }}>
          {loading ? 'Refreshing…' : '↻ Refresh'}
        </button>
      </div>

      <div style={{ display: 'flex', gap: 0, borderBottom: `1px solid ${c.border}`, marginBottom: 28 }}>
        {DEVOPS_TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '10px 18px', fontSize: 13, fontWeight: 500, cursor: 'pointer',
            background: 'transparent', border: 'none', borderBottom: `2px solid ${tab === t.id ? c.signal : 'transparent'}`,
            color: tab === t.id ? c.textPrimary : c.textMuted, transition: 'color 150ms', marginBottom: -1,
          }}>{t.label}</button>
        ))}
      </div>

      {error && <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, padding: '12px 16px', color: '#EF4444', fontSize: 13, marginBottom: 20 }}>{error}</div>}

      {tab === 'overview' && <OverviewTab status={status} loading={loading} />}
      {tab === 'model' && <ModelTab />}
      {tab === 'logs' && <LogsTab />}
      {tab === 'queue' && <QueueHealthTab />}
    </div>
  )
}
